import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { ChevronDown } from 'lucide-react';
import { SvgIcon } from '@/components';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { APP_LOCALES, getLocaleMeta } from '@/constants/locales';
import { persistPreferredLocale } from '@/utils/persistPreferredLocale';

const LanguageSwitcher = ({ colorScheme = 'dark' }) => {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const currentLang = getLocaleMeta(
    i18n.language && i18n.language.startsWith('fr') ? 'fr' : 'en'
  );
  const flagName = currentLang.label;

  const handleSelect = (code) => {
    persistPreferredLocale(code);
    setOpen(false);
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className={cn(
            "h-auto p-0 gap-1.5",
            colorScheme === 'light' && "text-[#E8EDEC] hover:text-[#E8EDEC] hover:bg-transparent",
            colorScheme === 'dark' && "text-[#002818] hover:text-[#002818] hover:bg-transparent"
          )}
        >
          <SvgIcon 
            name={flagName} 
            path="i18n" 
            size={20} 
            className="rounded-[2px] w-5 h-[13px]" 
          />
          <span className="font-semibold text-[15px] leading-[100%]">
            {currentLang.label}
          </span>
          <ChevronDown 
            className={cn(
              "w-[15px] h-[15px] transition-transform duration-200",
              open && "rotate-180",
              colorScheme === 'light' ? "text-[#E8EDEC]" : "text-[#888888]"
            )} 
          />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="min-w-[120px]">
        {APP_LOCALES.map((lang) => (
          <DropdownMenuItem
            key={lang.code}
            onClick={() => handleSelect(lang.code)}
            className={cn(
              "min-w-[120px] h-10 pl-[15px] cursor-pointer text-[#002818]",
              lang.code === currentLang.code && "bg-[#E8EDEC] text-(--app-color-primary)"
            )}
          >
            <SvgIcon 
              name={lang.label} 
              path="i18n" 
              size={20} 
              className="rounded-[2px] w-5 h-[13px]" 
            />
            <span>{lang.full}</span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

LanguageSwitcher.propTypes = {
  colorScheme: PropTypes.oneOf(['light', 'dark']),
};

export default LanguageSwitcher;
